import interfaceIdentifiers from "../../constants/interfaceIdentifiers";
import { definitionSelectors } from "../../modules/definition";
import { typeSelectors } from "../../modules/definition/types";
import preprocessorQuery from "../../utils/preprocessorQuery";
import { getFlashPlaceholder, getIconPlaceholder, getTipIdGenerator, Placeholders } from "../utils";
import getSharedTip, { SharedTipIdentifiers } from "./sharedTips";

const isDesktop = preprocessorQuery.isDesktop();

// -----------------------------------------------------
// IMPORTANT NOTES:
//
// GROUP_GATE / accessGate:
//    * expects a function with signature:
//        state => { return true; }
//    * return true to show tutorial
//    * return false to block tutorial and show message
//    * if function is null, will show tutorial
//
// resolve:
//    * expects a function with signature:
//       (action, newState, oldState) => { return true; }
//    * return true to mark the tip as resolved
//    * batch actions will be broken down by the time
//      this function is called, so you will receive the
//      individual actions
//
// -----------------------------------------------------

// Group ID
const GROUP_ID = "IMPORT_WRITING_PROJECT";
const GROUP_LABEL = "Import Writing Project";
const GROUP_DESCRIPTION = `Sync your timeline with your ${Placeholders.AVAILABLE_WRITING_APPS} project to import existing data.`;
const GROUP_GATE = state =>
  definitionSelectors.isNarrativeAllowed(state) && typeSelectors.getOrderedNarrativeTypes(state).size >= 2;
const GROUP_GATE_MESSAGE = "You must have Track Independent Narrative enabled to begin this tutorial.";
const GROUP_APPLICABLE_GATE = state => definitionSelectors.isNarrativeAllowed(state);
const GROUP_APPLICABLE_GATE_MESSAGE =
  "This tutorial only applies to timelines with Track Independent Narrative enabled.";
const GROUP_COMPLETED_MESSAGE =
  "Great work! Now that you have imported your existing work, it is time to develop your timeline further.";
const GROUP_LINKS = [
  {
    label: "Learn more about syncing",
    path: "https://help.timeline.app/article/158-syncing-introduction"
  },
  {
    label: "Preparing a project for syncing",
    path: "https://help.timeline.app/article/227-preparing-an-existing-writing-project-for-syncing"
  },
  { label: "Getting started with syncing", path: "https://help.timeline.app/article/216-getting-started-with-syncing" },
  { label: "Sync settings", path: "https://help.timeline.app/article/217-sync-settings" }
];

// Create an ID generator
// this will make IDs of the form: "GROUP_ID / TIP_ID"
const idGenerator = getTipIdGenerator(GROUP_ID);

let tips = [];
if (isDesktop) {
  tips = [
    getSharedTip(SharedTipIdentifiers.PREPARE_SYNC_PROJECT, GROUP_ID),
    getSharedTip(SharedTipIdentifiers.SELECT_SYNC_PROJECT, GROUP_ID),
    {
      id: idGenerator("VIEW_SYNC_PROJECT"),
      label: `View your ${Placeholders.WRITING_APP} project in the binder`,
      steps: [
        {
          label: `Click on the ${getFlashPlaceholder(interfaceIdentifiers.SYNC, "sync icon")} ${getIconPlaceholder(
            "sidebarSync"
          )} in the Sidebar to view the contents of your ${Placeholders.WRITING_APP} project in the Sync Panel`,
          isNumbered: true
        },
        {
          label: `A green ${getIconPlaceholder(
            "statusAdded",
            "statusAdded"
          )} icon indicates new documents that will be imported into your timeline's narrative`,
          isNumbered: true
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true
    },
    getSharedTip(SharedTipIdentifiers.MARK_DOCUMENTS_TO_IGNORE, GROUP_ID),
    getSharedTip(SharedTipIdentifiers.FIRST_TIME_SYNC, GROUP_ID),
    getSharedTip(SharedTipIdentifiers.SYNC_OPEN_NARRATIVE_VIEW, GROUP_ID),
    getSharedTip(SharedTipIdentifiers.CHANGE_SECTION_LABEL_SETTINGS, GROUP_ID),
    getSharedTip(SharedTipIdentifiers.CHANGE_NARRATIVE_SYNC_AGAIN, GROUP_ID),
    getSharedTip(SharedTipIdentifiers.CHANGE_PROJECT_SYNC_AGAIN, GROUP_ID)
  ];
} else {
  tips = [];
}

export default {
  id: GROUP_ID,
  label: GROUP_LABEL,
  description: GROUP_DESCRIPTION,
  accessGate: GROUP_GATE,
  gateMessage: GROUP_GATE_MESSAGE,
  applicableGate: GROUP_APPLICABLE_GATE,
  gateApplicableMessage: GROUP_APPLICABLE_GATE_MESSAGE,
  completedMessage: GROUP_COMPLETED_MESSAGE,
  links: GROUP_LINKS,
  tips
};
